import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  MessageCircle, 
  X, 
  Send, 
  Loader2, 
  Bot, 
  Sparkles,
  Minimize2,
  Maximize2,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { API, getAuthHeaders } from '../utils/api';

// Lightweight markdown renderer for assistant messages (headers, bold, bullets, numbered).
function renderMarkdown(text) {
  const lines = (text || '').split('\n');
  return lines.map((line, i) => {
    const trimmed = line.trim();
    if (!trimmed) return <div key={i} className="h-1.5" />;

    if (trimmed.startsWith('### ')) {
      return <h4 key={i} className="text-cyan-300 font-bold text-sm mt-2 mb-1">{trimmed.replace(/^###\s+/, '')}</h4>;
    }
    if (trimmed.startsWith('## ')) {
      return <h4 key={i} className="text-cyan-300 font-bold text-sm mt-2 mb-1">{trimmed.replace(/^##\s+/, '')}</h4>;
    }

    const bulletMatch = trimmed.match(/^[-*]\s+(.*)$/);
    const numMatch = trimmed.match(/^(\d+[).])\s+(.*)$/);
    const raw = bulletMatch ? bulletMatch[1] : numMatch ? numMatch[2] : trimmed;
    const parts = raw.split(/(\*\*[^*]+\*\*)/g).map((p, j) =>
      p.startsWith('**') && p.endsWith('**')
        ? <strong key={j} className="text-white font-semibold">{p.slice(2, -2)}</strong>
        : <span key={j}>{p}</span>
    );

    if (bulletMatch) {
      return (
        <div key={i} className="flex gap-2 leading-relaxed mb-0.5">
          <span className="text-cyan-500 mt-0.5">•</span><span>{parts}</span>
        </div>
      );
    }
    if (numMatch) {
      return (
        <div key={i} className="flex gap-2 leading-relaxed mb-0.5">
          <span className="text-cyan-400 font-mono">{numMatch[1]}</span><span>{parts}</span>
        </div>
      );
    }
    return <p key={i} className="leading-relaxed mb-0.5">{parts}</p>;
  });
}

export default function AIChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const savedSession = localStorage.getItem('chat_session_id');
    if (savedSession) {
      setSessionId(savedSession);
      loadChatHistory(savedSession);
    } else {
      setMessages([{
        role: 'assistant',
        content: `**Welcome to TradeForge! 👋**

I'm your AI trading assistant, here to help 24/7.

**Quick Help:**
• Ask me how to read any signal
• Questions about your subscription
• Platform features & settings
• Risk management basics

**Example:** "How do I read a XAUUSD BUY signal?"

What can I help you with today?`,
        created_at: new Date().toISOString()
      }]);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async (sid) => {
    try {
      const response = await axios.get(`${API}/chat/history/${sid}`, { timeout: 10000 });
      const data = response.data;
      if (data && data.messages && Array.isArray(data.messages) && !('type' in data && 'loc' in data)) {
        const validMessages = data.messages.filter(msg => 
          msg && typeof msg === 'object' && 
          typeof msg.content === 'string' &&
          typeof msg.role === 'string'
        );
        if (validMessages.length > 0) {
          setMessages(validMessages);
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const clearChat = () => {
    localStorage.removeItem('chat_session_id');
    setSessionId(null);
    setMessages([{
      role: 'assistant',
      content: `Chat cleared! 🔄 How can I help you today?`,
      created_at: new Date().toISOString()
    }]);
  };

  const handleSend = async () => {
    const userMsg = inputMessage.trim();
    if (!userMsg || loading) return;

    setInputMessage('');
    setLoading(true);

    setMessages(prev => [...prev, {
      role: 'user',
      content: userMsg,
      created_at: new Date().toISOString()
    }]);

    const appendDelta = (delta) => {
      setMessages(prev => {
        const copy = [...prev];
        const last = copy[copy.length - 1];
        if (last && last.role === 'assistant') {
          copy[copy.length - 1] = { ...last, content: last.content + delta };
        }
        return copy;
      });
    };

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ message: userMsg, session_id: sessionId })
      });
      if (!res.ok || !res.body) throw new Error('stream failed');

      // Add empty assistant bubble and switch from "thinking" to live streaming
      setMessages(prev => [...prev, { role: 'assistant', content: '', created_at: new Date().toISOString() }]);
      setStreaming(true);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamedAny = false;
      let errored = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          let evt;
          try { evt = JSON.parse(jsonStr); } catch (e) { continue; }
          if (evt.session_id && !sessionId) {
            setSessionId(evt.session_id);
            localStorage.setItem('chat_session_id', evt.session_id);
          }
          if (evt.delta) { streamedAny = true; appendDelta(evt.delta); }
          if (evt.error) { errored = true; }
        }
      }
      if (errored || !streamedAny) throw new Error('empty response');

    } catch (error) {
      console.error('Chat error:', error);
      toast.error('Failed to send message');
      // Drop an empty assistant placeholder if the stream produced nothing
      setMessages(prev => {
        const copy = [...prev];
        if (copy.length && copy[copy.length - 1].role === 'assistant' && !copy[copy.length - 1].content) {
          copy.pop();
        }
        return copy;
      });
    } finally {
      setStreaming(false);
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Quick action buttons
  const quickActions = [
    { label: "How to read signals?", icon: "📊" },
    { label: "Subscription help", icon: "💳" },
    { label: "Risk management", icon: "🛡️" }
  ];

  return (
    <>
      {/* Floating Trigger Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-6 z-[9999] group"
          data-testid="chatbot-trigger"
        >
          <div className="relative">
            {/* Glow effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full blur-lg opacity-50 group-hover:opacity-75 transition-opacity" />
            {/* Button */}
            <div className="relative w-14 h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-200">
              <MessageCircle className="w-6 h-6 text-white" />
            </div>
            {/* Pulse indicator */}
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-[#060A10] animate-pulse" />
          </div>
        </button>
      )}

      {/* Sidebar Chat Panel */}
      <div 
        className={`fixed top-0 right-0 h-full z-50 transform transition-all duration-300 ease-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } ${isExpanded ? 'w-[600px]' : 'w-[400px]'}`}
      >
        {/* Backdrop */}
        {isOpen && (
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm -z-10"
            onClick={() => setIsOpen(false)}
          />
        )}

        {/* Chat Container */}
        <div className="h-full bg-[#0A0E14] border-l border-[#1E293B] flex flex-col shadow-2xl">
          {/* Header */}
          <div className="flex-shrink-0 p-4 border-b border-[#1E293B] bg-gradient-to-r from-[#0D131C] to-[#0A0E14]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center border border-cyan-500/30">
                    <Sparkles className="w-6 h-6 text-cyan-400" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0A0E14]" />
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg font-['Manrope']">TradeForge AI</h3>
                  <p className="text-xs text-green-400 font-mono flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    Online • Claude
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={clearChat}
                  className="w-8 h-8 text-slate-400 hover:text-white hover:bg-[#1E293B]"
                  title="Clear chat"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="w-8 h-8 text-slate-400 hover:text-white hover:bg-[#1E293B]"
                  title={isExpanded ? "Minimize" : "Expand"}
                >
                  {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsOpen(false)}
                  className="w-8 h-8 text-slate-400 hover:text-white hover:bg-[#1E293B]"
                  data-testid="close-chat-btn"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-[#1E293B] scrollbar-track-transparent">
            {messages.map((msg, idx) => {
              const content = typeof msg?.content === 'string' ? msg.content : '';
              const role = typeof msg?.role === 'string' ? msg.role : 'assistant';
              
              return (
                <div
                  key={idx}
                  className={`flex ${role === 'user' ? 'justify-end' : 'justify-start'} animate-in slide-in-from-bottom-2 duration-200`}
                >
                  {role === 'assistant' && (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mr-2 flex-shrink-0 border border-cyan-500/20">
                      <Bot className="w-4 h-4 text-cyan-400" />
                    </div>
                  )}
                  <div
                    data-testid={role === 'assistant' ? 'assistant-message' : 'user-message'}
                    className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      role === 'user'
                        ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-br-md'
                        : 'bg-[#151B26] text-slate-200 border border-[#1E293B] rounded-bl-md'
                    }`}
                  >
                    {role === 'assistant'
                      ? <div className="text-sm">{renderMarkdown(content)}</div>
                      : <p className="text-sm whitespace-pre-wrap leading-relaxed">{content}</p>}
                  </div>
                </div>
              );
            })}
            
            {loading && !streaming && (
              <div className="flex justify-start animate-in slide-in-from-bottom-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-600/20 flex items-center justify-center mr-2 border border-cyan-500/20">
                  <Bot className="w-4 h-4 text-cyan-400" />
                </div>
                <div className="bg-[#151B26] border border-[#1E293B] rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}} />
                      <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}} />
                    </div>
                    <span className="text-xs text-slate-500">Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions (only show when few messages) */}
          {messages.length <= 2 && !loading && (
            <div className="flex-shrink-0 px-4 pb-2">
              <p className="text-xs text-slate-500 mb-2">Quick questions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInputMessage(action.label);
                      inputRef.current?.focus();
                    }}
                    className="px-3 py-1.5 bg-[#151B26] border border-[#1E293B] rounded-full text-xs text-slate-300 hover:border-cyan-500/50 hover:text-cyan-400 transition-all"
                  >
                    {action.icon} {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="flex-shrink-0 p-4 border-t border-[#1E293B] bg-[#0D131C]">
            <div className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask anything about trading..."
                  rows={1}
                  className="w-full bg-[#151B26] border border-[#1E293B] rounded-xl px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/50 placeholder:text-slate-500 disabled:opacity-50 transition-all"
                  disabled={loading}
                  data-testid="chat-input"
                  style={{ minHeight: '48px', maxHeight: '120px' }}
                />
              </div>
              <Button
                onClick={handleSend}
                disabled={!inputMessage.trim() || loading}
                className="h-12 w-12 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white rounded-xl shadow-lg shadow-cyan-500/20 disabled:opacity-50 disabled:shadow-none transition-all"
                data-testid="send-chat-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </Button>
            </div>
            <p className="text-[10px] text-slate-600 mt-2 text-center">
              AI responses are for educational purposes only. Not financial advice.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
